import React, {createContext, useCallback, useContext, useReducer} from "react";
import {AppContext} from './AppContext';

const initialState = {
    bulkJobs: {},
    dataDownloads: {},
    metaDownloads: {},
    finishedBulkJobIds: [],
    subscriptionReady: false,
}

const BulkJobsStateContext = createContext(initialState);
const BulkJobsDispatchContext = createContext(initialState);

const reducer = (state, action) => {
    switch (action.type) {
        case 'add':
            const currentBulkJob = state.bulkJobs[action.bulkJob.guid];
            const terminalBulkJobStates = ['processed', 'errored', 'failed', 'canceled'];
            const currentBulkJobTime = Date.parse(currentBulkJob?.updated_at);
            const addedBulkJobTime = Date.parse(action.bulkJob.updated_at);
            const addedBulkJobIsNewer = Number.isFinite(currentBulkJobTime) &&
                Number.isFinite(addedBulkJobTime) &&
                addedBulkJobTime > currentBulkJobTime;
            const addedBulkJobIsOlder = Number.isFinite(currentBulkJobTime) &&
                Number.isFinite(addedBulkJobTime) &&
                addedBulkJobTime < currentBulkJobTime;
            const currentBulkJobTerminal = currentBulkJob && (
                currentBulkJob.done ||
                currentBulkJob.canceled ||
                terminalBulkJobStates.includes(currentBulkJob.aasm_state)
            );
            const addedBulkJobTerminal = (
                action.bulkJob.done ||
                action.bulkJob.canceled ||
                terminalBulkJobStates.includes(action.bulkJob.aasm_state)
            );
            // A fast job can finish over ActionCable before the POST response
            // is added. Do not overwrite that terminal update with a pending
            // response snapshot, while still accepting a newer server state.
            if(addedBulkJobIsOlder || (currentBulkJobTerminal && !addedBulkJobTerminal && !addedBulkJobIsNewer)) {
                // Cable progress does not carry all API identity metadata. Even
                // an older create/processing response must identify this as a
                // real BulkJob so the app-level monitor can recover lost cable
                // updates. Never merge its older progress or terminal fields.
                const metadata = {};
                ['organization_id', 'action', 'refresh_scopes'].forEach(key => {
                    if(currentBulkJob[key] == null && action.bulkJob[key] != null) {
                        metadata[key] = action.bulkJob[key];
                    }
                });
                if(!Object.keys(metadata).length) return state;
                return {...state, bulkJobs: {
                    ...state.bulkJobs,
                    [action.bulkJob.guid]: {...currentBulkJob, ...metadata},
                }};
            }

            state.bulkJobs[action.bulkJob.guid] = {...currentBulkJob, ...action.bulkJob}
            return {...state, bulkJobs: {...state.bulkJobs}};

        case 'setSubscriptionReady':
            return {...state, subscriptionReady: action.ready};

        // Received from ActionCable.jsx subscription:
        case 'progress':
            const bj = state.bulkJobs[action.guid] || {guid: action.guid}
            const terminalStates = ['processed', 'errored', 'failed', 'canceled'];
            const alreadyTerminal = bj.done || bj.canceled || terminalStates.includes(bj.aasm_state);
            const incomingTerminal = action.done || action.canceled || terminalStates.includes(action.aasm_state);
            const currentStateTime = Date.parse(bj.updated_at);
            const incomingStateTime = Date.parse(action.updated_at);
            const incomingStateIsNewer = Number.isFinite(currentStateTime) &&
                Number.isFinite(incomingStateTime) &&
                incomingStateTime > currentStateTime;
            const incomingStateIsOlder = Number.isFinite(currentStateTime) &&
                Number.isFinite(incomingStateTime) &&
                incomingStateTime < currentStateTime;
            // Polls and ActionCable can cross in either direction. Server
            // updated_at is the ordering authority whenever both updates have
            // one, regardless of whether either state is terminal.
            if(incomingStateIsOlder) return state;

            // A polling response can have been requested before a terminal
            // ActionCable update. Never let that stale response regress the
            // job, while allowing a newer server transition to resume a
            // Sidekiq retry from errored to processing.
            if(alreadyTerminal && !incomingTerminal && !incomingStateIsNewer) return state;

            bj.progress = action.progress;
            if (action.activity?.organization_id != null) bj.organization_id = action.activity.organization_id;
            bj.total_assets = action.total_assets
            bj.added = action.added;
            bj.removed = action.removed;
            bj.done = action.done;
            [
                'removal',
                'removed_count',
                'skipped_count',
                'failed_count',
                'operation_counts',
                'refresh_scopes',
                'aasm_state',
                'updated_at',
                'asset_group_move',
                'selected_asset_group_count',
                'processed_asset_groups',
                'affected_asset_groups_count',
                'reindexing_asset_groups_count',
                'indexing_pending',
                'indexing_status'
            ].forEach(key => {
                if(Object.prototype.hasOwnProperty.call(action, key)) {
                    bj[key] = action[key];
                }
            });
            if(Object.prototype.hasOwnProperty.call(action, 'error')) {
                bj.error = action.error;
            }
            bj.warnings = action.warnings;
            bj.canceled = action.canceled;
            bj.lastUpdate = Date.now();
            state.bulkJobs[action.guid] = bj

            if(bj.progress === 100 && action.done) {
                if(!state.finishedBulkJobIds.includes(action.guid)) {
                    state.finishedBulkJobIds.push(action.guid)
                }
            }

            return {...state, bulkJobs: {...state.bulkJobs}};

        case 'dataDownload':
            state.dataDownloads[action.data.guid] = action.data
            return {...state, dataDownloads: {...state.dataDownloads}}

        case 'metaDownloadProgress':
            state.metaDownloads[action.data.guid] = action.data
            return {...state, metaDownloads: {...state.metaDownloads}}

        default:
            return state
    }
}

const freshState = () => ({
    ...initialState,
    bulkJobs: {},
    dataDownloads: {},
    metaDownloads: {},
    finishedBulkJobIds: [],
});

const scopedReducer = (store, event) => {
    if(event.type === 'changeUser') {
        return {userId: event.userId, generation: store.generation + 1, state: freshState()};
    }
    // POSTs, polls, and cable callbacks from a previous login must not seed
    // the next user's state, even if the same user logs back in later.
    if(event.generation !== store.generation) return store;
    const state = reducer(store.state, event.action);
    return state === store.state ? store : {...store, state};
};

const BulkJobsProvider = ({children})=> {
    const {state: appState} = useContext(AppContext);
    const userId = appState?.currentUser?.id ?? null;
    const [store, dispatchScoped] = useReducer(scopedReducer, userId, userId => ({
        userId, generation: 0, state: freshState(),
    }));
    // Reset before children render under the new identity. An effect would
    // expose the old jobs for one render and start requests as the new user.
    if(store.userId !== userId) dispatchScoped({type: 'changeUser', userId});
    const dispatch = useCallback(action => dispatchScoped({
        generation: store.generation, action,
    }), [store.generation]);
    return (
        <BulkJobsStateContext.Provider value={store.state}>
            <BulkJobsDispatchContext.Provider value={dispatch}>
                {children}
            </BulkJobsDispatchContext.Provider>
        </BulkJobsStateContext.Provider>
    )
}

function useBulkJobsState() {
    const context = useContext(BulkJobsStateContext)
    if (context === undefined) {
        throw new Error('useBulkJobsState must be used within a Provider')
    }
    return context
}

function useBulkJobsDispatch() {
    const context = useContext(BulkJobsDispatchContext)
    if (context === undefined) {
        throw new Error('useBulkJobsDispatch must be used within a Provider')
    }
    return context
}

export {
    initialState,
    reducer,
    BulkJobsProvider,
    useBulkJobsState,
    useBulkJobsDispatch
};
